import { useRouter } from 'next/router'
import { useRef, useState } from 'react'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Link } from '@wartek-id/link'
import { Shimmer } from '@wartek-id/shimmer'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'

import {
  API_PATH_SUPPORTING_DOCUMENT,
  useAPIGetSupportingDocument,
  useAPIUpsertSupportingDocument,
} from 'api/kinerja/useAPIKinerja'
import PDFViewerDialog from 'app/KinerjaPage/pages/UploadBuktiDukung/components/FileUpload/PDFViewerDialog'
import PanelInfo from 'app/KinerjaPage/pages/PelaksanaanV2/components/PanelInfo'
import useMediaManager from 'app/KinerjaShared/hooks/useMediaManager'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import useSKPPelaksanaanTrackEvent from 'app/KinerjaPage/hooks/useSKPPelaksanaanTrackEvent'
import { Header } from 'app/KinerjaPage/pages/PelaksanaanV2/components/Header'
import { REQUIRED_SUPPORTING_DOCUMENT_TYPES } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/supportingDocuments'
import { getSatuanPendidikanByNpsn } from 'app/KinerjaShared/utils/profile'
import useDemoContext from 'app/KinerjaPage/hooks/useDemoContext'
import { PANDUAN_PENGELOLAAN_GURU_UNGGAH_BUKTI_DUKUNG_KS_URL } from 'app/KinerjaShared/utils/panduan'

import type {
  ISupportingDocument,
  ISupportingDocumentTypes,
} from 'types/kinerja-pelaksanaan'

type KumpulkanDokumenProps = {
  npsn: string
}

export function KumpulkanDokumen({ npsn }: KumpulkanDokumenProps) {
  const router = useRouter()

  const { profile, isLoading: isLoadingProfile } = useRoleContext()
  const { isDemoMode } = useDemoContext()

  const {
    isFetching: isLoadingSupportingDocument,
    data: supportingDocumentData,
  } = useAPIGetSupportingDocument({
    params: {
      npsn,
    },
    options: {
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  return (
    <div className="px-8 pb-8 pt-12 bg-gray-10 min-h-full">
      <div className="max-w-6xl mx-auto">
        <Header
          title="Kumpulkan Dokumen"
          className="mb-9 print:hidden"
          rightNode={
            <PanelInfo
              isLoading={isLoadingProfile}
              label="Satuan Pendidikan"
              value={getSatuanPendidikanByNpsn(profile, npsn, isDemoMode)}
            />
          }
          onBack={() => {
            router.back()
          }}
        />

        <section className="mb-5 rounded-lg bg-white p-6">
          <Text color="subdued" className="mb-3">
            <strong>Catatan:</strong> Anda dapat mengumpulkan dokumen yang sudah
            tersedia dulu saja di satuan pendidikan Anda.
          </Text>
          <div className="space-y-6 max-w-3xl">
            {REQUIRED_SUPPORTING_DOCUMENT_TYPES.map(({ type, label }) => (
              <fieldset className="space-y-1" key={type}>
                <Text
                  variant="subheading"
                  className="!uppercase"
                  as="label"
                  htmlFor={type}
                >
                  {label}
                </Text>
                {isLoadingSupportingDocument ? (
                  <Shimmer height="64px" width="100%" />
                ) : (
                  <DokumenUploadField
                    label={label}
                    npsn={npsn}
                    type={type}
                    value={supportingDocumentData.data?.documents?.find(
                      (supportingDocument) => supportingDocument.type === type
                    )}
                  />
                )}
                <Text>
                  Cek&nbsp;
                  <Link
                    href={PANDUAN_PENGELOLAAN_GURU_UNGGAH_BUKTI_DUKUNG_KS_URL}
                    target="_blank"
                  >
                    Panduan
                  </Link>
                </Text>
              </fieldset>
            ))}
          </div>
        </section>
      </div>
    </div>
  )
}

type DokumenUploadFieldProps = {
  label: string
  npsn: string
  type: ISupportingDocumentTypes
  value?: ISupportingDocument
}

function DokumenUploadField({
  label,
  npsn,
  type,
  value,
}: DokumenUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [pdfViewerDialogShown, setPdfViewerDialogShown] = useState(false)

  const [document, setDocument] = useState(value)

  const { trackEvent } = useSKPPelaksanaanTrackEvent()

  const toast = useToast()
  const queryClient = useQueryClient()

  const upsertMutation = useAPIUpsertSupportingDocument(npsn, {
    useErrorBoundary: false,
  })

  const handleUpsert = (document: ISupportingDocument, label: string) => {
    // Upsert a single document
    upsertMutation.mutate(
      {
        documents: [
          { type: document.type, mediaManagerId: document.mediaManagerId },
        ],
      },
      {
        onSuccess: async () => {
          toast({
            message: `Berhasil kumpulkan ${label}!`,
            type: 'success',
          })

          setDocument(document)

          // Invalidate the GET query
          await queryClient.invalidateQueries(API_PATH_SUPPORTING_DOCUMENT, {
            refetchActive: false, // avoid refetch on current page
          })
        },
        onError: () => {
          toast({ message: `Gagal kumpulkan ${label}. Coba lagi` })
        },
      }
    )
  }

  const { handleUpload, isLoading: isUploading } = useMediaManager({
    uploadType: 'SUPPORTING_DOCUMENT',
    onSuccess: (res) => {
      handleUpsert(
        {
          type,
          fileUrl: res.signedUrl,
          fileName: res.originalName,
          mediaManagerId: res.id,
        },
        label
      )
    },
    onError: () => {
      toast({ message: `Gagal kumpulkan ${label}. Coba lagi` })
    },
  })

  const handleChooseDocument = () => {
    trackEvent({
      activity_type: isUploaded
        ? 'penilaian_ks_kdd_click_edit'
        : 'penilaian_ks_kdd_click_unggah',
      screen_name: 'ks_detail_kelengkapan_dokumen',
      event_trigger: 'ks_detail_kelengkapan_dokumen',
    })
    inputRef?.current?.click()
  }

  const handleShowDocument = () => {
    trackEvent({
      activity_type: 'penilaian_ks_kdd_click_cek_detail',
      screen_name: 'ks_detail_kelengkapan_dokumen',
      event_trigger: 'ks_detail_kelengkapan_dokumen',
    })
    setPdfViewerDialogShown(true)
  }

  const isUploaded = !!document
  const isLoading = upsertMutation.isLoading || isUploading
  const isEditDisabled = isLoading || !isUploaded
  const isUploadDisabled = isLoading
  const isShowDisabled = isLoading

  return (
    <>
      <div className="rounded border border-dashed py-2 pr-4 pl-3 border-default gap-2 flex items-center">
        <input
          type="file"
          className="invisible absolute top-0"
          accept="application/pdf"
          id={type}
          ref={inputRef}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0]
            if (file) {
              handleUpload(file)
            }
          }}
        />
        {isLoading ? (
          <div className="flex-shrink-0">
            <Spinner size="sm" />
          </div>
        ) : isUploaded ? (
          <Icon className="text-gray-80">text_snippet</Icon>
        ) : (
          <Icon className="text-gray-80 rotate-45">attach_file</Icon>
        )}
        <Text
          className="w-full overflow-wrap-anywhere line-clamp-1"
          color="subdued"
        >
          {document?.fileName || 'Belum ada dokumen.'}
        </Text>
        {isUploaded ? (
          <Button
            color="white"
            size="lg"
            disabled={isShowDisabled}
            onClick={handleShowDocument}
          >
            Cek
          </Button>
        ) : (
          <Button
            color="black"
            size="lg"
            disabled={isUploadDisabled}
            onClick={handleChooseDocument}
          >
            Unggah
          </Button>
        )}
        <Button
          color="white"
          size="lg"
          disabled={isEditDisabled}
          onClick={handleChooseDocument}
        >
          Edit
        </Button>
      </div>

      <PDFViewerDialog
        url={document?.fileUrl}
        name={document?.fileName}
        isOpen={pdfViewerDialogShown && !!document?.fileUrl}
        onClose={() => setPdfViewerDialogShown(false)}
      />
    </>
  )
}
